import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllFamilies, createFamily, deleteFamily, addFamilyMember, removeFamilyMember, getClients, getUsers } from '../lib/api';
import { formatApiError, getInitials, maskPAN, getClientDisplayName } from '../lib/utils';
import { MEMBER_RELATIONSHIPS, AUM_RANGES } from '../lib/constants';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, UsersThree, UserPlus, Trash, Crown, X } from '@phosphor-icons/react';

export default function FamiliesPage() {
  const [families, setFamilies] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [removeMember, setRemoveMember] = useState(null);

  const [createForm, setCreateForm] = useState({ head_client_id: '', description: '', family_aum: '', account_manager: '', operations_manager: '', senior_account_manager: '', senior_operations_manager: '' });
  const [memberForm, setMemberForm] = useState({ client_id: '', relationship_to_head: '' });
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);

  const fetchData = async () => {
    try {
      const [familiesRes, clientsRes, usersRes] = await Promise.all([getAllFamilies(), getClients(), getUsers()]);
      setFamilies(familiesRes.data);
      setClients(clientsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreateFamily = async (e) => {
    e.preventDefault();
    if (!createForm.head_client_id) { toast.error('Please select a head of family'); return; }
    if (!createForm.family_aum) { toast.error('Family AUM is required'); return; }
    if (!createForm.account_manager) { toast.error('Account Manager is required'); return; }
    setSaving(true);
    try {
      const headClient = clients.find(c => c.id === createForm.head_client_id);
      const payload = {
        name: headClient?.full_name || 'Unnamed Family',
        head_client_id: createForm.head_client_id,
        description: createForm.description,
        family_aum: createForm.family_aum || undefined,
        account_manager: createForm.account_manager || undefined,
        operations_manager: createForm.operations_manager || undefined,
        senior_account_manager: createForm.senior_account_manager || undefined,
        senior_operations_manager: createForm.senior_operations_manager || undefined,
      };
      await createFamily(payload);
      toast.success('Family created successfully');
      setShowCreateModal(false);
      setCreateForm({ head_client_id: '', description: '', family_aum: '', account_manager: '', operations_manager: '', senior_account_manager: '', senior_operations_manager: '' });
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!memberForm.client_id) { toast.error('Please select a client'); return; }
    if (!memberForm.relationship_to_head) { toast.error('Please select a relationship'); return; }
    setSaving(true);
    try {
      await addFamilyMember(showAddMemberModal, memberForm);
      toast.success('Member added successfully');
      setShowAddMemberModal(null);
      setMemberForm({ client_id: '', relationship_to_head: '' });
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!removeMember) return;
    try {
      await removeFamilyMember(removeMember.familyId, removeMember.clientId);
      toast.success('Member removed');
      setRemoveMember(null);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDeleteFamily = async () => {
    if (!deleteId) return;
    try {
      await deleteFamily(deleteId);
      toast.success('Family deleted');
      setFamilies(families.filter(f => f.id !== deleteId));
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeleteId(null);
    }
  };

  const getAvailableClients = (family) => {
    const existingIds = [family.head_client_id, ...(family.member_ids || [])];
    return clients.filter(c => !existingIds.includes(c.id));
  };

  return (
    <div className="p-6 space-y-6" data-testid="families-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Family Management</h1>
          <p className="text-sm text-zinc-500 mt-1">Group related clients into families</p>
        </div>
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogTrigger asChild>
            <Button className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm" data-testid="create-family-button">
              <Plus size={18} className="mr-2" /> Create Family
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create New Family</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateFamily} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase font-semibold">Head of Family *</Label>
                <SearchableSelect
                  value={createForm.head_client_id}
                  onValueChange={(v) => setCreateForm(prev => ({ ...prev, head_client_id: v }))}
                  options={clients.map(c => ({ value: c.id, label: `${getClientDisplayName(c)} (${maskPAN(c.pan_number)})` }))}
                  placeholder="Select head client"
                  searchPlaceholder="Search clients..."
                  data-testid="family-head-select"
                />
                {createForm.head_client_id && (
                  <p className="text-xs text-zinc-500 mt-1">
                    Family name: <span className="font-medium text-zinc-700">{clients.find(c => c.id === createForm.head_client_id)?.full_name}</span>
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase font-semibold">Family AUM (Assets Under Management) *</Label>
                <SearchableSelect
                  value={createForm.family_aum || ''}
                  onValueChange={(v) => setCreateForm(prev => ({ ...prev, family_aum: v }))}
                  options={AUM_RANGES}
                  placeholder="Select AUM range"
                  data-testid="family-aum-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase font-semibold">Description</Label>
                <Textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional notes about this family"
                  rows={2} className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase font-semibold">Account Manager *</Label>
                <SearchableSelect
                  value={createForm.account_manager}
                  onValueChange={(v) => setCreateForm(prev => ({ ...prev, account_manager: v }))}
                  options={users.filter(u => u.status === 'active').map(u => ({ value: u.id, label: u.name }))}
                  placeholder="Select AM"
                  data-testid="family-am-select"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold">Operations Manager</Label>
                  <SearchableSelect
                    value={createForm.operations_manager}
                    onValueChange={(v) => setCreateForm(prev => ({ ...prev, operations_manager: v }))}
                    options={[{ value: '', label: 'None' }, ...users.filter(u => u.status === 'active').map(u => ({ value: u.id, label: u.name }))]}
                    placeholder="Select OM"
                    data-testid="family-om-select"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold">Senior Account Manager</Label>
                  <SearchableSelect
                    value={createForm.senior_account_manager}
                    onValueChange={(v) => setCreateForm(prev => ({ ...prev, senior_account_manager: v }))}
                    options={[{ value: '', label: 'None' }, ...users.filter(u => u.status === 'active').map(u => ({ value: u.id, label: u.name }))]}
                    placeholder="Select Sr. AM"
                    data-testid="family-sr-am-select"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold">Senior Operations Manager</Label>
                  <SearchableSelect
                    value={createForm.senior_operations_manager}
                    onValueChange={(v) => setCreateForm(prev => ({ ...prev, senior_operations_manager: v }))}
                    options={[{ value: '', label: 'None' }, ...users.filter(u => u.status === 'active').map(u => ({ value: u.id, label: u.name }))]}
                    placeholder="Select Sr. OM"
                    data-testid="family-sr-om-select"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-[#002FA7] text-white" data-testid="submit-family-button">
                  {saving ? 'Creating...' : 'Create Family'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="space-y-4">{[1, 2, 3].map(i => <Skeleton key={i} className="h-48" />)}</div>
      ) : families.length === 0 ? (
        <Card className="border-zinc-200 rounded-sm">
          <CardContent className="p-12 text-center">
            <UsersThree size={48} className="mx-auto text-zinc-300 mb-4" />
            <p className="text-zinc-500">No families created yet</p>
            <Button variant="link" onClick={() => setShowCreateModal(true)} className="text-[#002FA7] mt-2">Create your first family</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {families.map(family => (
            <Card key={family.id} className="border-zinc-200 rounded-sm" data-testid={`family-card-${family.id}`}>
              <CardHeader className="border-b border-zinc-100 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <UsersThree size={18} className="text-[#002FA7]" /> {family.name}
                  </CardTitle>
                  {family.description && <p className="text-xs text-zinc-500 mt-1">{family.description}</p>}
                  {family.family_aum && <p className="text-xs text-[#002FA7] font-medium mt-1">AUM: {family.family_aum}</p>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowAddMemberModal(family.id)} className="rounded-sm" data-testid={`add-member-${family.id}`}>
                    <UserPlus size={16} className="mr-1" /> Add Member
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteId(family.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                    <Trash size={16} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="p-4 bg-blue-50/50 border-b border-zinc-100">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-[#002FA7] text-white">{getInitials(family.head_client?.full_name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Link to={`/clients/${family.head_client_id}`} className="font-medium text-sm hover:text-[#002FA7]">
                          {family.head_client?.full_name || 'Unknown'}
                        </Link>
                        <Badge className="bg-amber-100 text-amber-700 text-xs"><Crown size={10} className="mr-1" /> Head</Badge>
                      </div>
                      <p className="text-xs text-zinc-500">{family.head_client?.phone} • {maskPAN(family.head_client?.pan_number)}</p>
                    </div>
                  </div>
                </div>
                {family.members?.length > 0 ? (
                  <div className="divide-y divide-zinc-100">
                    {family.members.map((member, idx) => (
                      <div key={idx} className="p-4 flex items-center justify-between hover:bg-zinc-50">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-zinc-200 text-zinc-600">{getInitials(member.client?.full_name)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <Link to={`/clients/${member.client_id}`} className="font-medium text-sm hover:text-[#002FA7]">{member.client?.full_name || 'Unknown'}</Link>
                            <p className="text-xs text-zinc-500">{member.relationship_to_head} • {member.client?.phone}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setRemoveMember({ familyId: family.id, clientId: member.client_id })} className="text-zinc-400 hover:text-red-600">
                          <X size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-zinc-400 text-sm">No members added yet</div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Member Modal */}
      <Dialog open={!!showAddMemberModal} onOpenChange={() => { setShowAddMemberModal(null); setMemberForm({ client_id: '', relationship_to_head: '' }); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Family Member</DialogTitle></DialogHeader>
          <form onSubmit={handleAddMember} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase font-semibold">Select Client *</Label>
              <SearchableSelect
                value={memberForm.client_id}
                onValueChange={(v) => setMemberForm(prev => ({ ...prev, client_id: v }))}
                options={showAddMemberModal ? getAvailableClients(families.find(f => f.id === showAddMemberModal) || {}).map(c => ({ value: c.id, label: c.full_name })) : []}
                placeholder="Select client"
                searchPlaceholder="Search clients..."
                data-testid="member-client-select"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase font-semibold">Relationship to Head *</Label>
              <SearchableSelect
                value={memberForm.relationship_to_head}
                onValueChange={(v) => setMemberForm(prev => ({ ...prev, relationship_to_head: v }))}
                options={MEMBER_RELATIONSHIPS}
                placeholder="Select relationship"
                data-testid="member-relationship-select"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddMemberModal(null)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#002FA7] text-white" data-testid="submit-member-button">
                {saving ? 'Adding...' : 'Add Member'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!removeMember} onOpenChange={() => setRemoveMember(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Member</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to remove this member from the family?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveMember} className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Family</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this family? This will not delete the clients.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteFamily} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
